import { useState, useEffect, useRef } from 'react';
import { BarChart3, Download, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import JsBarcode from 'jsbarcode';
import { useToolUsage } from '@/hooks/useToolUsage';

const FORMATS = [
  { value: 'CODE128', label: 'CODE128 (General)' },
  { value: 'EAN13', label: 'EAN-13' },
  { value: 'UPC', label: 'UPC-A' },
  { value: 'CODE39', label: 'CODE39' },
  { value: 'ITF14', label: 'ITF-14' },
];

const BarcodeGenerator = () => {
  useToolUsage('barcode-generator');
  const [text, setText] = useState('1234567890128');
  const [format, setFormat] = useState('CODE128');
  const svgRef = useRef<SVGSVGElement>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!svgRef.current || !text) return;
    try {
      JsBarcode(svgRef.current, text, {
        format,
        lineColor: '#000',
        width: 2,
        height: 80,
        displayValue: true,
        fontSize: 14,
        margin: 10,
      });
      setError('');
    } catch (e: any) {
      setError(e.message || 'Invalid input for this format');
    }
  }, [text, format]);

  const download = () => {
    if (!svgRef.current) return;
    const svg = svgRef.current;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;
    const data = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      const a = document.createElement('a');
      a.href = canvas.toDataURL('image/png');
      a.download = `barcode_${text}.png`;
      a.click();
      toast.success('Downloaded!');
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(data)));
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-primary/20">
          <BarChart3 className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Barcode Generator</h1>
          <p className="text-muted-foreground">Generate barcodes in various formats</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardContent className="pt-6 space-y-4">
            <div>
              <Label className="text-sm mb-2 block">Format</Label>
              <Select value={format} onValueChange={setFormat}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FORMATS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm mb-2 block">Data</Label>
              <Input value={text} onChange={e => setText(e.target.value)} placeholder="Enter text or number" />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button onClick={download} disabled={!!error || !text} className="w-full">
              <Download className="h-4 w-4 mr-2" /> Download PNG
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6 flex items-center justify-center bg-white rounded-lg min-h-[200px]">
            <svg ref={svgRef} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BarcodeGenerator;
